<template>
  <div class="kpi-call-line-container">
    <Loading v-if="loading"></Loading>
    <template v-else>
      <div class="table-container" v-if="canView">
        <ve-table style="width: 100%;" :scroll-width="900" :columns="columns" :table-data="tableData"
          row-key-field-name="rowKey" fixed-header max-height="100vh" :rowStyleOption="{ stripe: true }" />
      </div>
      <div v-else class="no-auth">
        <img src="https://file.iviewui.com/iview-pro/icon-403.svg">
        <p>抱歉，您访问的页面不存在</p>
      </div>
    </template>
  </div>
</template>

<script>
import DayJs from 'dayjs'
import { mapGetters } from 'vuex'
import { hasOneOf } from '@/util'
import Loading from '@/components/Loading'
import { fetchRoleCodeList } from '@/services/UserService'
import { getKpiCallLinStatistic } from '@/services/KpiService'
export default {
  components: {
    Loading
  },
  data() {
    return {
      loading: false,
      roleCodeList: [],
      tableData: []
    }
  },
  computed: {
    ...mapGetters([
      'achievementRate'
    ]),
    columns() {
      return [
        {
          field: '',
          key: 'expand',
          title: '',
          width: 30,
          align: 'center',
          fixed: 'left',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return row.isLeaf ? '' : h('div', {
              style: {
                'font-size': '16px',
                position: 'relative',
                top: '-2px'
              },
              on: {
                click: () => {
                  this.onExpand(row, rowIndex)
                }
              }
            }, row.canExpand ? (row.isExpand ? '-' : '+') : '')
          }
        },
        {
          title: '线路',
          key: 'callLine',
          tree: true,
          fixed: 'left',
          align: 'left',
          width: 90,
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('span', {
              style: {
                position: 'relative',
                left: row.isLeaf ? '10px' : 0
              }
            }, row.teamName || row.callLine || '未知')
          }
        },
        {
          title: '接通率',
          key: 'connRate',
          width: 100,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            const percent = Number(row.connRate * 100).toFixed(2)
            return h('span', `${percent}%`)
          }
        },
        {
          title: '呼叫量',
          key: 'callCnt',
          field: 'callCnt',
          width: 60,
          align: 'center'
        },
        {
          title: '接通量',
          key: 'connCnt',
          field: 'connCnt',
          width: 60,
          align: 'center'
        },
        {
          title: '呼叫受限',
          key: 'callRestrictedCnt',
          field: 'callRestrictedCnt',
          width: 80,
          align: 'center'
        },
        {
          title: '线路限制',
          key: 'userUnreachableCnt',
          field: 'userUnreachableCnt',
          width: 80,
          align: 'center'
        },
        {
          title: '空号',
          key: 'emptyNumberCnt',
          field: 'emptyNumberCnt',
          width: 60,
          align: 'center'
        },
        {
          title: '欠费未接听',
          key: 'unansweredCnt',
          field: 'unansweredCnt',
          width: 80,
          align: 'center'
        },
        {
          title: '用户关机',
          key: 'calledToShutDownCnt',
          field: 'calledToShutDownCnt',
          width: 80,
          align: 'center'
        },
        {
          title: '用户拒接',
          key: 'userRejectsCnt',
          field: 'userRejectsCnt',
          width: 80,
          align: 'center'
        },
        {
          title: '用户未接听',
          key: 'notAnswerCnt',
          field: 'notAnswerCnt',
          width: 80,
          align: 'center'
        },
        {
          title: '用户未响应',
          key: 'userUnaccessCnt',
          field: 'userUnaccessCnt',
          width: 80,
          align: 'center'
        },
        {
          title: '坐席未接听',
          key: 'agentTimeoutCnt',
          field: 'agentTimeoutCnt',
          width: 80,
          align: 'center'
        }
      ]
    },
    canView() {
      return this.roleCodeList && this.roleCodeList.length &&
        hasOneOf(this.roleCodeList, ['T', 'CALL_LINE_MANAGER'])
    }
  },
  async mounted() {
    document.title = '线路指标'
    const openId = localStorage.getItem('openId')
    this.loading = true
    await this.onInitRoleCodeList()
    const response = await getKpiCallLinStatistic({
      openId,
      startDate: DayJs().format('YYYY-MM-DD') + ' 00:00:00',
      endDate: DayJs().format('YYYY-MM-DD') + ' 23:59:59'
    })
    this.loading = false
    this.tableData = (response.data || []).map(item => {
      if (item.callLine === '汇总' || !item.callLine) {
        return {
          ...item,
          canExpand: false
        }
      }
      return {
        ...item,
        canExpand: true
      }
    })
  },
  methods: {
    async onInitRoleCodeList() {
      const openId = localStorage.getItem('openId')
      const response = await fetchRoleCodeList({ openId })
      this.roleCodeList = response.data || []
    },
    async onExpand(row, rowIndex) {
      const openId = localStorage.getItem('openId')
      if (!row.isExpand) {
        this.$set(row, 'isExpand', true)
        this.$toast.loading({
          duration: 0,
          message: '数据加载中...',
          forbidClick: true
        })
        const response = await getKpiCallLinStatistic({
          openId,
          startDate: DayJs().format('YYYY-MM-DD') + ' 00:00:00',
          endDate: DayJs().format('YYYY-MM-DD') + ' 23:59:59',
          callLine: row.callLine
        })
        this.$toast.clear()
        const kpiList = (response.data || []).map(item => ({
          ...item,
          isLeaf: true
        }))
        this.tableData.splice(rowIndex + 1, 0, ...kpiList)
      } else {
        for (let index = rowIndex + 1; index < this.tableData.length; index++) {
          if (this.tableData[index].isLeaf) {
            this.tableData[index] = undefined
          } else {
            break
          }
        }
        this.$set(row, 'isExpand', false)
        this.tableData = this.tableData.filter(item => !!item)
      }
    }
  }
}
</script>
<style lang="stylus">
  .kpi-call-line-container {
    @media screen and (orientation portrait) {
      .table-container {
        .ve-table-header-tr, .ve-table-body-tr, .ve-table-expand-tr {
          height 30px !important
        }
        .ve-table-header-th, .ve-table-body-td, .ve-table-expand-td-content {
          font-size 12px !important
          padding 6px !important
        }
        .iconfont-vet {
          font-size 12px !important
        }
        .ve-table-row-expand-icon {
          width 15px !important
          height 15px !important
        }
      }
    }
    @media screen and (orientation landscape) {
      .table-container {
        .ve-table-header-tr, .ve-table-body-tr, .ve-table-expand-tr {
          height 20px !important
        }
        .ve-table-header-th, .ve-table-body-td, .ve-table-expand-td-content {
          font-size 8px !important
          padding 4px !important
        }
        .iconfont-vet {
          font-size 8px !important
        }
        .ve-table-row-expand-icon {
          width 10px !important
          height 10px !important
        }
      }
    }
    .no-auth {
      height 100vh
      display flex
      flex-direction column
      align-items center
      padding-top 100px
      img {
        width 80%
      }
      p {
        margin-top: 16px;
        color: #808695;
        font-size: 20px;
        line-height: 28px;
      }
    }
  }
</style>
